/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, NgZone, OnChanges} from '@angular/core';
import {handleCopyModalResult} from '../common/util/handle-copy-modal-result';
import {ToastrService} from 'ngx-toastr';
import * as _ from 'lodash';

import {AngularModalService} from '../common/modal/angular-modal.service';
import {wireModelCommsModelActions} from '../common/controller/model-comms-actions';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {LineItemTemplateCopyModalComponent} from './line-item-template-copy-modal.component';
import template from './line-item-list-context-actions.component.html';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-line-item-list-context-actions',
  template
})
export class LineItemListContextActionsComponent implements OnChanges {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;
  @Input() readOnly: boolean;
  @Input() sectionParam: string;

  hasLineItemTemplates = false;
  effectiveReadOnly = false;
  newLineItemParams: Record<string, any> = {};
  newLineItemHref = '';

  constructor(
    private readonly toastr: ToastrService,
    private readonly $state: StateService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly angularModalService: AngularModalService,
    private readonly ngZone: NgZone
  ) {
    this.hasLineItemTemplates = LocalNetworkInfo.obtain().getInfo('lineItemTemplatesCount') > 0;
    this.updateNewLineItemParams();
  }

  ngOnChanges(): void {
    this.resolveReadOnly();
    this.updateNewLineItemParams();
    if (this.modelComms) {
      wireModelCommsModelActions(this.modelComms);
    }
  }

  createFromTemplate(): void {
    this.openCopyModal(true, true);
  }

  goToNewLineItem(event: Event): void {
    event.preventDefault();
    this.$state.go('app.adv.line-items.new', this.newLineItemParams);
  }

  private updateNewLineItemParams(): void {
    const viewType = this.LocalNetworkProfile.isCalt() && this.LocalUserPermissions.canUseCaltLineItemViewType2() ? 2 : 1;
    const params: Record<string, any> = {
      viewType,
      full: viewType === 2
    };
    if (this.model?.id) {
      params.orderId = this.model.id;
      params.orderName = this.model.name;
      params.advertiserId = _.get(this.model, ['advertiser', 'id']);
      params.advertiserName = _.get(this.model, ['advertiser', 'name']);
    }
    this.newLineItemParams = params;
    // Hard-code the list URL because context components do not have a UI-Router
    // reference point for a relative ui-sref such as ".new".
    this.newLineItemHref = '/line-items/new';
  }

  private openCopyModal(copyToLineItem: boolean, fromLineItemList: boolean): void {
    const modalRef = this.angularModalService.show(LineItemTemplateCopyModalComponent, {
      initialState: {
        model: this.model,
        copyToLineItem,
        fromLineItemList
      },
      class: 'modal-lg'
    });
    (modalRef.content as LineItemTemplateCopyModalComponent).onCopied.subscribe((result) => {
      handleCopyModalResult(() => {
        this.ngZone.run(() => {
          const url = copyToLineItem ? 'app.adv.line-items.line-item' : 'app.adv.line-item-templates.line-item-template';
          const text = copyToLineItem ? 'line item' : 'line item template';
          if (result.destination) {
            this.$state.go(url, {id: result.newId}, {inherit: false});
          } else {
            this.toastr.success('A copy of this ' + text + ' was successfully created.', text.toUpperCase() + ' Copied');
          }
        });
      });
    });
  }

  private resolveReadOnly(): void {
    const sectionPerms = this.LocalUserPermissions.getSectionPerms();
    const sectionParam = this.sectionParam;
    const specificEditableParameter = sectionParam + 'Editable';
    if (sectionParam && Object.prototype.hasOwnProperty.call(sectionPerms, specificEditableParameter)) {
      this.effectiveReadOnly = !sectionPerms[specificEditableParameter];
    } else if (sectionParam && Object.prototype.hasOwnProperty.call(sectionPerms, sectionParam)) {
      this.effectiveReadOnly = !sectionPerms[sectionParam];
    } else {
      this.effectiveReadOnly = !!this.readOnly;
    }
  }
}
